import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { api } from '../services/api';

const STEPS = [
  {
    target: '[data-tour="sidebar-funnel"]',
    title: '🚀 Bem-vindo ao CRM Totvs Moda!',
    content: 'Este é o seu painel de vendas. Comece acessando o Funil de Vendas para visualizar todos os seus leads em formato Kanban.',
    placement: 'right',
  },
  {
    target: '[data-tour="sidebar-myarea"]',
    title: '📊 Minha Área',
    content: 'Na Minha Área você encontra um resumo dos seus leads prioritários, ações do dia e metas mensais — tudo em um só lugar.',
    placement: 'right',
  },
  {
    target: '[data-tour="funnel-add-lead"]',
    title: '➕ Criando um Lead',
    content: 'Clique aqui para adicionar um novo lead. Preencha o CNPJ e os dados serão preenchidos automaticamente pela Receita Federal.',
    placement: 'bottom',
    fallbackContent: 'Para criar um lead, clique no botão "Novo Lead" no topo da página do Funil.',
  },
  {
    target: '[data-tour="sidebar-tasks"]',
    title: '✅ Tarefas',
    content: 'Gerencie suas tarefas e próximas ações aqui. Leads com ações vencidas ficam destacados no funil.',
    placement: 'right',
  },
  {
    target: '[data-tour="sidebar-reports"]',
    title: '📈 Relatórios',
    content: 'Acompanhe o desempenho do time, metas e forecast de receita nos relatórios. Disponíveis em diferentes visões.',
    placement: 'right',
  },
  {
    target: null,
    title: '🎯 Tudo pronto!',
    content: 'Você está pronto para usar o CRM. Explore os menus laterais para acessar leads, propostas, playbooks e muito mais. Boas vendas!',
    placement: 'center',
  },
];

function getTargetRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  return el.getBoundingClientRect();
}

function TooltipBox({ step, stepIndex, total, onNext, onPrev, onSkip, onFinish }) {
  const rect = step.target ? getTargetRect(step.target) : null;
  const isCentered = !rect || step.placement === 'center';

  let style = {};
  if (!isCentered && rect) {
    const OFFSET = 12;
    if (step.placement === 'right') {
      style = { top: rect.top + rect.height / 2, left: rect.right + OFFSET, transform: 'translateY(-50%)' };
    } else if (step.placement === 'bottom') {
      style = { top: rect.bottom + OFFSET, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
    } else if (step.placement === 'left') {
      style = { top: rect.top + rect.height / 2, left: rect.left - OFFSET, transform: 'translate(-100%, -50%)' };
    } else {
      style = { top: rect.bottom + OFFSET, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
    }
  }

  const isLast = stepIndex === total - 1;
  const content = (rect === null && step.fallbackContent) ? step.fallbackContent : step.content;

  if (isCentered) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
        <div className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 w-80 max-w-[90vw]">
          <TourContent step={{ ...step, content }} stepIndex={stepIndex} total={total}
            onNext={onNext} onPrev={onPrev} onSkip={onSkip} onFinish={onFinish} isLast={isLast} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed z-[9999] pointer-events-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 w-72"
      style={{ ...style, position: 'fixed' }}
    >
      {/* Arrow indicator */}
      {step.placement === 'right' && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-0 h-0"
          style={{ borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: '8px solid white' }} />
      )}
      {step.placement === 'bottom' && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-0 h-0"
          style={{ borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid white' }} />
      )}
      <TourContent step={{ ...step, content }} stepIndex={stepIndex} total={total}
        onNext={onNext} onPrev={onPrev} onSkip={onSkip} onFinish={onFinish} isLast={isLast} />
    </div>
  );
}

function TourContent({ step, stepIndex, total, onNext, onPrev, onSkip, onFinish, isLast }) {
  return (
    <>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-800 leading-tight pr-2">{step.title}</h3>
        <button onClick={onSkip} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
          <X size={14} />
        </button>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed mb-4">{step.content}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{stepIndex + 1} / {total}</span>
        <div className="flex gap-2">
          {stepIndex > 0 && (
            <button onClick={onPrev}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded">
              <ChevronLeft size={12} /> Anterior
            </button>
          )}
          {isLast ? (
            <button onClick={onFinish}
              className="flex items-center gap-1 text-xs bg-[#00DBFF] text-white px-3 py-1 rounded-lg font-medium hover:bg-[#00c4e5]">
              <Check size={12} /> Concluir
            </button>
          ) : (
            <button onClick={onNext}
              className="flex items-center gap-1 text-xs bg-[#00DBFF] text-white px-3 py-1 rounded-lg font-medium hover:bg-[#00c4e5]">
              Próximo <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
      {/* Progress dots */}
      <div className="flex gap-1 justify-center mt-3">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === stepIndex ? 'bg-[#00DBFF]' : 'bg-gray-200'}`} />
        ))}
      </div>
    </>
  );
}

function HighlightOverlay({ target }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!target) { setRect(null); return; }
    const el = document.querySelector(target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect(r);
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [target]);

  if (!rect) {
    return <div className="fixed inset-0 z-[9998] bg-black/40" />;
  }

  const PAD = 6;
  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      {/* top */}
      <div className="absolute bg-black/40" style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - PAD) }} />
      {/* bottom */}
      <div className="absolute bg-black/40" style={{ top: rect.bottom + PAD, left: 0, right: 0, bottom: 0 }} />
      {/* left */}
      <div className="absolute bg-black/40" style={{ top: rect.top - PAD, left: 0, width: Math.max(0, rect.left - PAD), height: rect.height + PAD * 2 }} />
      {/* right */}
      <div className="absolute bg-black/40" style={{ top: rect.top - PAD, left: rect.right + PAD, right: 0, height: rect.height + PAD * 2 }} />
      {/* highlight ring */}
      <div className="absolute rounded-lg ring-2 ring-[#00DBFF] ring-offset-0" style={{
        top: rect.top - PAD, left: rect.left - PAD,
        width: rect.width + PAD * 2, height: rect.height + PAD * 2,
      }} />
    </div>
  );
}

export default function OnboardingTour({ onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const step = STEPS[stepIndex];

  async function finish() {
    setVisible(false);
    try { await api.completeOnboarding(); } catch { /* ignore */ }
    onComplete?.();
  }

  function skip() { finish(); }

  function next() {
    if (stepIndex < STEPS.length - 1) setStepIndex(i => i + 1);
  }

  function prev() {
    if (stepIndex > 0) setStepIndex(i => i - 1);
  }

  if (!visible) return null;

  return (
    <>
      <HighlightOverlay target={step.target} />
      <TooltipBox
        step={step}
        stepIndex={stepIndex}
        total={STEPS.length}
        onNext={next}
        onPrev={prev}
        onSkip={skip}
        onFinish={finish}
      />
    </>
  );
}
